import { useState, useEffect, useRef } from 'react';
import {
  Bot,
  Brain,
  Workflow,
  AlertCircle,
  CheckCircle,
  Database,
  X,
  Activity,
  Zap,
  Edit,
  Trash2,
  Plus,
  Save,
  Settings,
} from 'lucide-react';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge } from '@/components/Badge';
import { useToast } from '@/contexts/ToastContext';
import { confirmDelete, showDeleteSuccess } from '@/utils/deleteConfirmation';

interface Agent {
  id: string;
  name: string;
  model: string;
  description: string;
  temperature: number;
  maxTokens: number;
  status: 'active' | 'inactive';
  features: {
    queryAnalysis: boolean;
    mcpServerSelection: boolean;
    contextMerging: boolean;
    responseGeneration: boolean;
  };
  mcpServers: string[];
  avgLatency?: string;
  queriesHandled?: number;
  successRate?: number;
}

export function AgentsConfig() {
  const { showToast } = useToast();
  const modeChangeTimerRef = useRef<number | null>(null);

  // Load from localStorage on mount
  const [ragMode, setRagMode] = useState<'basic' | 'agentic' | 'multi-agent'>(() => {
    const saved = localStorage.getItem('ragMode');
    return (saved as 'basic' | 'agentic' | 'multi-agent') || 'agentic';
  });
  const [pendingMode, setPendingMode] = useState<'basic' | 'agentic' | 'multi-agent' | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Agent management state
  const [agents, setAgents] = useState<Agent[]>([
    {
      id: '1',
      name: 'Claude Query Analyzer',
      model: 'claude-sonnet-4.6-20250929',
      description: 'Main AI agent that analyzes queries, selects relevant MCP servers, and generates responses',
      temperature: 0.7,
      maxTokens: 4000,
      status: 'active',
      features: {
        queryAnalysis: true,
        mcpServerSelection: true,
        contextMerging: true,
        responseGeneration: true,
      },
      mcpServers: ['Pinecone', 'PDF Content', 'OE Database', 'Regulations', 'Previous Exams', 'Glossary', 'ANBIMA API'],
      avgLatency: '800ms',
      queriesHandled: 12547,
      successRate: 98.5,
    },
  ]);

  const [isAddingAgent, setIsAddingAgent] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [agentForm, setAgentForm] = useState<Partial<Agent>>({
    name: '',
    model: 'claude-sonnet-4.6-20250929',
    description: '',
    temperature: 0.7,
    maxTokens: 4000,
    status: 'active',
    features: {
      queryAnalysis: true,
      mcpServerSelection: true,
      contextMerging: true,
      responseGeneration: true,
    },
    mcpServers: [],
  });

  const availableModels = [
    { value: 'claude-sonnet-4.6-20250929', label: 'Claude Sonnet 4.5' },
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-4o', label: 'GPT-4o' },
  ];

  const availableMCPServers = [
    'Pinecone',
    'PDF Content',
    'OE Database',
    'Regulations',
    'Previous Exams',
    'Glossary',
    'ANBIMA API',
  ];

  // Persist to localStorage when ragMode changes
  useEffect(() => {
    localStorage.setItem('ragMode', ragMode);
  }, [ragMode]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (modeChangeTimerRef.current) {
        clearTimeout(modeChangeTimerRef.current);
      }
    };
  }, []);

  const handleModeChange = (newMode: 'basic' | 'agentic' | 'multi-agent') => {
    if (newMode === ragMode) return;

    if (newMode === 'basic' || newMode === 'multi-agent') {
      showToast(
        'warning',
        'Mode Not Available',
        `${newMode === 'basic' ? 'Basic RAG' : 'Multi-Agent RAG'} is not currently supported. Only Agentic RAG is available.`
      );
      return;
    }

    setPendingMode(newMode);
    setShowConfirmation(true);
  };

  const confirmModeChange = () => {
    if (pendingMode) {
      setRagMode(pendingMode);
      setShowConfirmation(false);
      setPendingMode(null);

      // Clear any existing timer
      if (modeChangeTimerRef.current) {
        clearTimeout(modeChangeTimerRef.current);
      }

      // Show success toast after a brief delay
      modeChangeTimerRef.current = window.setTimeout(() => {
        showToast(
          'success',
          'RAG Mode Changed',
          `RAG mode successfully changed to ${pendingMode === 'agentic' ? 'Agentic RAG' : pendingMode}`
        );
        modeChangeTimerRef.current = null;
      }, 500);
    }
  };

  const handleAddAgent = () => {
    setAgentForm({
      name: '',
      model: 'claude-sonnet-4.6-20250929',
      description: '',
      temperature: 0.7,
      maxTokens: 4000,
      status: 'active',
      features: {
        queryAnalysis: true,
        mcpServerSelection: true,
        contextMerging: true,
        responseGeneration: true,
      },
      mcpServers: [],
    });
    setEditingAgent(null);
    setIsAddingAgent(true);
  };

  const handleEditAgent = (agent: Agent) => {
    setAgentForm(agent);
    setEditingAgent(agent);
    setIsAddingAgent(true);
  };

  const handleSaveAgent = () => {
    if (!agentForm.name || !agentForm.model) {
      showToast('warning', 'Validation Error', 'Please fill in all required fields.');
      return;
    }

    if (editingAgent) {
      // Update existing agent
      setAgents(agents.map(a => a.id === editingAgent.id ? { ...editingAgent, ...agentForm } as Agent : a));
      showToast('success', 'Agent Updated', `Agent "${agentForm.name}" has been updated successfully.`);
    } else {
      // Add new agent
      const newAgent: Agent = {
        ...agentForm as Agent,
        id: Date.now().toString(),
      };
      setAgents([...agents, newAgent]);
      showToast('success', 'Agent Created', `Agent "${agentForm.name}" has been created successfully.`);
    }

    setIsAddingAgent(false);
    setEditingAgent(null);
  };

  const handleDeleteAgentClick = async (agent: Agent) => {
    if (agents.length === 1) {
      showToast('warning', 'Cannot Delete', 'Cannot delete the last agent. At least one agent must be configured.');
      return;
    }

    const confirmed = await confirmDelete({
      title: 'Delete Agent?',
      itemName: agent.name,
    });

    if (confirmed) {
      setAgents(agents.filter(a => a.id !== agent.id));
      showDeleteSuccess(`Agent "${agent.name}" has been deleted successfully.`);
    }
  };

  const handleToggleMCPServer = (server: string) => {
    const current = agentForm.mcpServers || [];
    const updated = current.includes(server)
      ? current.filter(s => s !== server)
      : [...current, server];
    setAgentForm({ ...agentForm, mcpServers: updated });
  };

  const ragModes = [
    {
      id: 'basic',
      name: 'Basic RAG',
      icon: '📄',
      description: 'Simple retrieval and generation pipeline',
      features: ['Embedding', 'Vector Search', 'LLM Generation'],
    },
    {
      id: 'agentic',
      name: 'Agentic RAG',
      icon: '🤖',
      description: 'Single AI agent with memory and planning',
      features: ['Agent Runtime', 'Memory', 'Planning', 'Tools'],
    },
    {
      id: 'multi-agent',
      name: 'Multi-Agent RAG',
      icon: '🎯',
      description: 'Multiple specialized agents working together',
      features: ['Aggregator', 'Specialist Agents', 'MCP Servers', 'Advanced Planning'],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'RAG System', path: '/rag' },
        { label: 'Agent Configuration' }
      ]} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agent Configuration</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure AI agents for your RAG system architecture
          </p>
        </div>
        {ragMode === 'agentic' && (
          <Tooltip content="Add a new AI agent to your system">
            <button
              onClick={handleAddAgent}
              className="btn-primary flex items-center text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Agent
            </button>
          </Tooltip>
        )}
      </div>

      {/* RAG Mode Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Workflow className="w-6 h-6 text-primary-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">RAG Architecture Mode</h2>
            <p className="text-sm text-gray-600">Choose your RAG system architecture</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {ragModes.map((mode) => {
            const isDisabled = mode.id === 'basic' || mode.id === 'multi-agent';
            const isActive = ragMode === mode.id;

            return (
            <Tooltip
              key={mode.id}
              content={
                isDisabled
                  ? `${mode.name} is coming soon - not yet implemented`
                  : isActive
                  ? `${mode.name} is currently active`
                  : `Switch to ${mode.name} mode`
              }
            >
              <button
                onClick={() => handleModeChange(mode.id as any)}
                disabled={isDisabled}
                className={`text-left p-6 rounded-xl border-2 transition-all relative ${
                  isActive
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : isDisabled
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="text-4xl mb-3">{mode.icon}</div>
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{mode.name}</h3>
                  {(mode.id === 'basic' || mode.id === 'multi-agent') && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-4">{mode.description}</p>
                <div className="space-y-1">
                  {mode.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-xs text-gray-500">
                      <CheckCircle className="w-3 h-3 mr-1.5 text-green-600" />
                      {feature}
                    </div>
                  ))}
                </div>
                {isActive && (
                  <div className="mt-4 pt-4 border-t border-primary-200">
                    <span className="inline-flex items-center text-xs font-semibold text-primary-700">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Currently Active
                    </span>
                  </div>
                )}
              </button>
            </Tooltip>
          );
          })}
        </div>
      </div>

      {/* Agents List */}
      {ragMode === 'agentic' && (
        <>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Bot className="w-6 h-6 text-primary-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Configured Agents ({agents.length})</h2>
                <p className="text-sm text-gray-600">Manage AI agents in your Agentic RAG system</p>
              </div>
            </div>

            <div className="space-y-4">
              {agents.map((agent) => (
                <div
                  key={agent.id}
                  className="bg-gradient-to-br from-orange-50 via-blue-50 to-white rounded-xl border-2 border-orange-200 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-#f09628 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Bot className="w-10 h-10 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{agent.name}</h3>
                          <Tooltip content={`Model: ${agent.model}`}>
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg border border-blue-200">
                              {availableModels.find(m => m.value === agent.model)?.label || agent.model}
                            </span>
                          </Tooltip>
                          <StatusBadge status={agent.status} />
                        </div>
                        <p className="text-sm text-gray-700 mb-3 max-w-2xl">{agent.description}</p>
                        <div className="flex items-center space-x-6 text-xs">
                          <Tooltip content="Number of connected MCP servers">
                            <div className="flex items-center space-x-2">
                              <Database className="w-4 h-4 text-#ea7500" />
                              <span className="text-gray-600">
                                <span className="font-semibold text-gray-900">{agent.mcpServers?.length || 0}</span> MCP Servers
                              </span>
                            </div>
                          </Tooltip>
                          <Tooltip content="Temperature setting (randomness)">
                            <div className="flex items-center space-x-2">
                              <Settings className="w-4 h-4 text-blue-600" />
                              <span className="text-gray-600">
                                Temp: <span className="font-semibold text-gray-900">{agent.temperature}</span>
                              </span>
                            </div>
                          </Tooltip>
                          <Tooltip content="Maximum tokens per response">
                            <div className="flex items-center space-x-2">
                              <Activity className="w-4 h-4 text-green-600" />
                              <span className="text-gray-600">
                                Max Tokens: <span className="font-semibold text-gray-900">{agent.maxTokens}</span>
                              </span>
                            </div>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Tooltip content="Edit agent configuration">
                        <button
                          onClick={() => handleEditAgent(agent)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-transparent hover:border-primary-200"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Delete this agent">
                        <button
                          onClick={() => handleDeleteAgentClick(agent)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Agent Capabilities */}
                  <div className="grid grid-cols-4 gap-3 pt-4 border-t border-orange-200">
                    {Object.entries(agent.features).map(([key, enabled]) => (
                      <div key={key} className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center space-x-2 mb-1">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ${
                            enabled ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            {enabled ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <X className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <h4 className="text-xs font-semibold text-gray-900">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </h4>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Connected MCP Servers */}
                  {agent.mcpServers && agent.mcpServers.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-orange-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        Connected MCP Servers
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {agent.mcpServers.map((server, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-white text-gray-700 text-xs font-medium rounded border border-gray-200"
                          >
                            {server}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Quick Action Cards */}
          <div className="grid grid-cols-3 gap-4">
            <Tooltip content="View and manage all MCP server connections">
              <a
                href="/rag/tools"
                className="card hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-primary-500"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-primary-100 rounded-lg">
                    <Database className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">MCP Servers</h3>
                    <p className="text-xs text-gray-600">Manage 7 knowledge sources</p>
                  </div>
                  <StatusBadge status="active" />
                </div>
              </a>
            </Tooltip>

            <Tooltip content="Monitor agent tasks and execution logs">
              <a
                href="/rag/execution"
                className="card hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-blue-500"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Activity className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Task Monitor</h3>
                    <p className="text-xs text-gray-600">View execution logs</p>
                  </div>
                  <StatusBadge status="active" />
                </div>
              </a>
            </Tooltip>

            <Tooltip content="Test queries and explore agent responses">
              <a
                href="/rag/query-playground"
                className="card hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-green-500"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Zap className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Query Playground</h3>
                    <p className="text-xs text-gray-600">Test agent responses</p>
                  </div>
                  <StatusBadge status="active" />
                </div>
              </a>
            </Tooltip>
          </div>
        </>
      )}

      {/* Add/Edit Agent Modal */}
      {isAddingAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-orange-50 to-blue-50 border-b border-orange-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Bot className="w-6 h-6 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingAgent ? 'Edit Agent' : 'Add New Agent'}
                </h3>
              </div>
              <button
                onClick={() => setIsAddingAgent(false)}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form className="p-6 space-y-6" onSubmit={(e) => { e.preventDefault(); handleSaveAgent(); }}>
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Basic Information</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Agent Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={agentForm.name || ''}
                      onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                      placeholder="e.g., Claude Query Analyzer"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Model <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={agentForm.model || ''}
                      onChange={(e) => setAgentForm({ ...agentForm, model: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                      required
                    >
                      {availableModels.map((model) => (
                        <option key={model.value} value={model.value}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={agentForm.description || ''}
                    onChange={(e) => setAgentForm({ ...agentForm, description: e.target.value })}
                    placeholder="Describe what this agent does..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Temperature (0.0 - 1.0)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={agentForm.temperature || 0.7}
                      onChange={(e) => setAgentForm({ ...agentForm, temperature: parseFloat(e.target.value) })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">Higher = more creative, Lower = more deterministic</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      min="100"
                      max="8000"
                      step="100"
                      value={agentForm.maxTokens || 4000}
                      onChange={(e) => setAgentForm({ ...agentForm, maxTokens: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum response length</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={agentForm.status || 'active'}
                    onChange={(e) => setAgentForm({ ...agentForm, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Agent Capabilities */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Agent Capabilities</h4>
                <div className="grid grid-cols-2 gap-3">
                  {agentForm.features && Object.entries(agentForm.features).map(([key, enabled]) => (
                    <label key={key} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setAgentForm({
                          ...agentForm,
                          features: { ...agentForm.features, [key]: e.target.checked } as any
                        })}
                        className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* MCP Server Selection */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  Connected MCP Servers ({agentForm.mcpServers?.length || 0} selected)
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {availableMCPServers.map((server) => (
                    <label key={server} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="checkbox"
                        checked={agentForm.mcpServers?.includes(server) || false}
                        onChange={() => handleToggleMCPServer(server)}
                        className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-900">{server}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setIsAddingAgent(false)}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingAgent ? 'Update Agent' : 'Create Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
                <h3 className="text-lg font-semibold text-gray-900">Confirm RAG Mode Change</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to change to <strong>{pendingMode === 'agentic' ? 'Agentic RAG' : pendingMode}</strong> mode?
                This will affect how queries are processed and may impact performance temporarily.
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    setShowConfirmation(false);
                    setPendingMode(null);
                  }}
                  className="flex-1 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModeChange}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Confirm Change
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Basic RAG Message */}
      {ragMode === 'basic' && (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <Brain className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Basic RAG Mode - No Agents Required
          </h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            In Basic RAG mode, the system uses simple embedding and retrieval without AI agents. Switch
            to Agentic or Multi-Agent mode to configure intelligent agents.
          </p>
        </div>
      )}

    </div>
  );
}
